import { z } from "zod";
import {
  ESCORT_ROUTE_IDS,
  escortRouteLength,
  type EscortMissionView,
  type EscortProgress,
  type EscortProof,
  type EscortTerms,
} from "@frontier/content/escort-contracts";
import { contractNpcArchetypeSchema } from "./contract-npc-schema.js";

const metricSchema = z.number().finite().min(0).max(1_000_000_000);
const identitySchema = z.string().min(1).max(200);
export const escortRouteIdSchema = z.enum(ESCORT_ROUTE_IDS);
export const escortTermsSchema = z
  .object({
    routeId: escortRouteIdSchema,
    version: z.literal("escort-routes-v1"),
    path: z
      .array(
        z.object({ x: z.number().finite(), y: z.number().finite() }).strict(),
      )
      .min(2)
      .max(64),
    speed: metricSchema.positive(),
    transportHp: metricSchema.positive(),
    escortRadius: metricSchema.positive(),
    bindUntilRatio: z.number().finite().min(0).max(1),
    minimumDistanceRatio: z.number().finite().min(0).max(1),
    minimumContribution: metricSchema,
    waves: z
      .array(
        z
          .object({
            at: z.number().finite().min(0).max(1),
            archetypes: z.array(contractNpcArchetypeSchema).min(1).max(16),
          })
          .strict(),
      )
      .max(16),
  })
  .strict()
  .refine((terms) => escortRouteLength(terms) > 0, {
    message: "Escort path must have a physical length",
  }) satisfies z.ZodType<EscortTerms>;

export const escortProofSchema = z
  .object({
    routeId: escortRouteIdSchema,
    encounterId: identitySchema,
    distance: metricSchema,
    damage: metricSchema,
    healing: metricSchema,
  })
  .strict() satisfies z.ZodType<EscortProof>;

export const escortProgressSchema = escortProofSchema.extend({
  phase: z.enum(["bound", "qualified", "arrived", "lost"]),
}) satisfies z.ZodType<EscortProgress>;

export const escortMissionViewSchema = z
  .object({
    status: z.enum([
      "waiting",
      "active",
      "confirming",
      "arrived",
      "lost",
      "cooldown",
    ]),
    encounterId: identitySchema.nullable(),
    transportId: identitySchema.nullable(),
    transportHp: metricSchema,
    transportMaxHp: metricSchema,
    distanceTravelled: metricSchema,
    routeLength: metricSchema,
    escortedDistance: metricSchema,
    damage: metricSchema,
    healing: metricSchema,
  })
  .strict()
  .refine((view) => view.transportHp <= view.transportMaxHp, {
    message: "Escort HP cannot exceed maximum HP",
  })
  .refine(
    (view) =>
      view.distanceTravelled <= view.routeLength + 0.001 &&
      view.escortedDistance <= view.routeLength + 0.001,
    {
      message: "Escort distances cannot exceed the physical route",
    },
  ) satisfies z.ZodType<EscortMissionView>;
